import { useEffect } from "react";
import { Link } from "react-router-dom";
import NavBar from "@/components/site/NavBar";
import Footer from "@/components/site/Footer";

const SECTIONS = [
  {
    id: "introduction",
    n: "01",
    title: "Introduction",
    body: (
      <>
        <p>
          Jos Family Law, Inc. (&ldquo;the Firm,&rdquo; &ldquo;we,&rdquo;
          &ldquo;us,&rdquo; &ldquo;our&rdquo; or &ldquo;Jos Family Law&rdquo;)
          operates the website located at{" "}
          <a
            href="https://www.josfamilylaw.com"
            target="_blank"
            rel="noreferrer"
            className="text-teal hover:text-ink transition-colors underline"
          >
            www.josfamilylaw.com
          </a>
          , along with related landing pages used for our advertising
          campaigns (together, the &ldquo;Site&rdquo;). Jos Family Law is
          incorporated in the State of California. This Privacy Policy
          explains what information we collect, how we use it, and the choices
          you have. By using the Site, you expressly agree / consent to the
          practices described in this policy.
        </p>
        <p>
          This policy applies to information collected through the Site. It
          does not apply to information we collect from clients in the course
          of representation, which is governed by our retainer agreement and
          applicable rules of professional conduct.
        </p>
      </>
    ),
  },
  {
    id: "information-we-collect",
    n: "02",
    title: "Information We Collect",
    body: (
      <ul>
        <li>
          <strong>Information you provide to us directly</strong>, such as when
          you fill out a contact form, request a consultation, call our office
          (including calls answered by our automated intake assistant), or
          chat with us through the Site. This may include your name, phone
          number, email address, and a description of your legal matter.
        </li>
        <li>
          <strong>Information collected automatically</strong>, including your
          IP address, browser and device type, pages viewed, time spent on the
          Site, referring URLs, and similar usage data. We collect this
          through cookies and similar technologies described in Section 4.
        </li>
        <li>
          <strong>Information from advertising platforms.</strong> When you
          click on or interact with one of our ads on Google or Meta
          (Facebook/Instagram), those platforms may share certain information
          with us, such as which ad you clicked and general demographic or
          interest data, in accordance with their own privacy policies.
        </li>
      </ul>
    ),
  },
  {
    id: "how-we-use",
    n: "03",
    title: "How We Use Your Information",
    body: (
      <>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Respond to your inquiry and schedule consultations</li>
          <li>Operate, maintain, and improve the Site</li>
          <li>Measure and improve the performance of our advertising campaigns</li>
          <li>
            Measure and improve the performance of our website using the
            metrics obtained through the information you provide
          </li>
          <li>Communicate with you about our services</li>
          <li>Comply with legal obligations and protect the Firm&rsquo;s legal rights</li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    n: "04",
    title: "Cookies and Tracking Technologies",
    body: (
      <>
        <p>
          The Site uses cookies, pixels, and similar tools, including the Meta
          Pixel and Google Ads/Analytics tags, to understand how visitors use
          the Site and to measure the effectiveness of our advertising. These
          tools may track activity across the Site and, in some cases, across
          other websites you visit.
        </p>
        <p>
          You can control cookies through your browser settings. If you
          disable cookies, some features of the Site may not function properly.
          If you wish to disable cookies from this site, you can do so using
          your browser. Disabling cookies may cause some features of the site
          to not function properly.
        </p>
        <p>
          We may also track information about your interaction with our
          website in web server logs, which is information regarding the
          activities on our website. The information may be automatically
          captured and saved electronically, including but not limited to the
          pages you viewed on our website; the date and time of your visit on
          our website; the browser used to get to our website; the area from
          where the contact originated such as your city, county, etc.; and
          other identifying metric information.
        </p>
      </>
    ),
  },
  {
    id: "how-we-share",
    n: "05",
    title: "How We Share Information",
    body: (
      <>
        <p>We do not sell your personal information. We may share information with:</p>
        <ul>
          <li>
            <strong>Service providers</strong> who help us operate the Site,
            manage client matters (including our case management software),
            host our website, or run our marketing campaigns, who are
            authorized to use the information only as needed to provide these
            services
          </li>
          <li>
            <strong>Advertising platforms</strong> such as Google and Meta,
            for analytics and ad performance measurement
          </li>
          <li>
            <strong>Legal and safety purposes</strong>, if required by law,
            court order, or to protect the rights, property, or safety of the
            Firm or others
          </li>
          <li>
            <strong>In connection with a business transfer</strong>, such as a
            merger, acquisition, or sale of assets
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "privacy-choices",
    n: "06",
    title: "Your Privacy Choices, Including for California Residents",
    body: (
      <p>
        Depending on where you live, you may have rights to request access to,
        deletion of, or correction of your personal information, and to opt
        out of certain uses of your information for targeted advertising. To
        make a request, contact us using the information in Section 12. We
        will respond consistent with applicable law.
      </p>
    ),
  },
  {
    id: "data-security",
    n: "07",
    title: "Data Security",
    body: (
      <p>
        We use reasonable administrative, technical, and physical safeguards
        designed to protect the information we collect. However, no method of
        transmission over the internet or electronic storage is completely
        secure, and we cannot guarantee absolute security.
      </p>
    ),
  },
  {
    id: "no-attorney-client",
    n: "08",
    title: "Submitting Information Through the Site Does Not Create an Attorney-Client Relationship",
    body: (
      <p>
        Sending an inquiry, filling out a contact form, or chatting with our
        automated assistant does not create an attorney-client relationship
        and is not protected by the attorney-client privilege. Please do not
        send confidential or sensitive information about your legal matter
        until an attorney-client relationship has been formally established
        through a signed engagement agreement.
      </p>
    ),
  },
  {
    id: "childrens-privacy",
    n: "09",
    title: "Children's Privacy",
    body: (
      <p>
        The Site is not directed to children under the age of 13, and we do
        not knowingly collect personal information from children.
      </p>
    ),
  },
  {
    id: "third-party-links",
    n: "10",
    title: "Third-Party Links",
    body: (
      <p>
        The Site may contain links to third-party websites. We are not
        responsible for the privacy practices or content of those sites, and
        we encourage you to review their privacy policies.
      </p>
    ),
  },
  {
    id: "do-not-track",
    n: "11",
    title: "Do Not Track",
    body: (
      <p>
        The receipt of any Do Not Track requests or signals in browsers does
        not in any way change our web services or change our tracking
        practices and we do not respond to &ldquo;Do Not Track&rdquo; browser
        signals. We may track user activity to help us analyze the metrics
        associated with our website and make improvements to our site. You
        can exercise other choices regarding the collection and use of your
        information by adjusting your own browser or device settings.
      </p>
    ),
  },
  {
    id: "sms-messaging",
    n: "12",
    title: "SMS / Text Messaging Consent",
    body: (
      <>
        <p>
          Jos Family Law sends SMS to prospective and existing clients about
          their legal matters: intake form links, appointment reminders,
          case-related updates, and replies to client questions. Clients opt
          in through any of three paths.
        </p>
        <p>
          <strong>(1) Website form.</strong> On our landing pages (for
          example,{" "}
          <a
            href="https://lp.josfamilylaw.com/child-custody-attorney-orange-county"
            target="_blank"
            rel="noreferrer"
            className="text-teal hover:text-ink transition-colors underline"
          >
            lp.josfamilylaw.com/child-custody-attorney-orange-county
          </a>
          ) the client enters their mobile number and checks an optional
          box — unchecked by default and not required to submit — that
          reads: &ldquo;I agree to receive text messages from Jos Family
          Law about my inquiry, including intake links, appointment
          reminders, and case updates. Message frequency varies. Msg &amp;
          data rates may apply. Reply STOP to opt out.&rdquo;
        </p>
        <p>
          <strong>(2) Website chat assistant.</strong> The client provides
          their number and replies &ldquo;Yes&rdquo; to a prompt asking
          them to confirm consent, with the same disclosures shown.
        </p>
        <p>
          <strong>(3) By phone.</strong> When a client calls our office,
          staff asks whether they consent to receive texts about their
          matter and records the consent; the first text identifies Jos
          Family Law and includes opt-out instructions.
        </p>
        <p>
          Message frequency varies. Message and data rates may apply. Reply
          STOP to opt out. See our{" "}
          <Link
            to="/terms-of-use"
            className="text-teal hover:text-ink transition-colors underline"
          >
            Terms of Use
          </Link>{" "}
          and this{" "}
          <Link
            to="/privacy-policy"
            className="text-teal hover:text-ink transition-colors underline"
          >
            Privacy Policy
          </Link>
          . Mobile information and opt-in consent are not shared with
          third parties for marketing or promotional purposes.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    n: "13",
    title: "Contact Us",
    body: (
      <>
        <p>
          If you have questions about this Privacy Policy or wish to make a
          request regarding your information, please contact us:
        </p>
        <div className="not-prose mt-6 border-l-2 border-teal pl-5 font-body text-[15px] leading-[1.8] text-ink">
          <p className="font-display italic text-2xl text-ink mb-2">
            Jos Family Law
          </p>
          <p>1918 W. Chapman Avenue, Suite 200, Orange, CA 92868</p>
          <p>
            Phone:{" "}
            <a
              href="tel:+17147337066"
              className="text-teal hover:text-ink transition-colors font-medium"
            >
              714-733-7066
            </a>
          </p>
        </div>
      </>
    ),
  },
  {
    id: "changes",
    n: "14",
    title: "Changes to This Policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time. The
        &ldquo;Effective Date&rdquo; at the top of this page reflects the date
        of the most recent revision. Continued use of the Site after changes
        are posted constitutes acceptance of the updated policy.
      </p>
    ),
  },
];

const LAST_UPDATED = "December 15, 2025";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="privacy-page" className="relative bg-paper text-ink">
      <NavBar />

      {/* Hero */}
      <section className="bg-paper pt-16 md:pt-24 pb-12 md:pb-16 border-b border-gray-200/60">
        <div className="max-w-[920px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex items-center gap-4 mb-8">
            <span className="w-10 h-px bg-teal" />
            <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-gray-600">
              Privacy Policy
            </p>
          </div>
          <h1 className="font-display text-[44px] md:text-[64px] lg:text-[76px] leading-[1.02] tracking-tight text-ink">
            How we handle{" "}
            <span className="italic">your information.</span>
          </h1>
          <p className="mt-6 font-mono text-[11px] tracking-[0.24em] uppercase text-gray-600">
            Last Updated · {LAST_UPDATED}
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-24">
        <div className="max-w-[920px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="space-y-16">
            {SECTIONS.map((s) => (
              <article
                key={s.id}
                id={s.id}
                data-testid={`privacy-section-${s.n}`}
                className="grid grid-cols-12 gap-6 md:gap-10 scroll-mt-24"
              >
                <div className="col-span-12 md:col-span-3">
                  <h2 className="font-display text-2xl md:text-[28px] leading-tight tracking-tight text-ink">
                    {s.title}
                  </h2>
                </div>
                <div
                  className="col-span-12 md:col-span-9 space-y-4 text-[16px] leading-[1.8] text-gray-800
                  [&_p]:text-[16px] [&_p]:leading-[1.8] [&_p]:text-gray-800
                  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-3 [&_ul]:marker:text-teal
                  [&_li]:text-[16px] [&_li]:leading-[1.75]
                  [&_strong]:text-ink [&_strong]:font-semibold"
                >
                  {s.body}
                </div>
              </article>
            ))}
          </div>

          {/* Back link */}
          <div className="mt-20 pt-10 border-t border-gray-200/60 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <Link
              to="/child-custody-attorney-orange-county"
              data-testid="privacy-back-home"
              className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-ink hover:text-teal transition-colors"
            >
              <span>← Back to Home</span>
            </Link>
            <a
              href="/child-custody-attorney-orange-county#consultation"
              className="inline-flex items-center justify-center bg-teal text-ink hover:bg-ink hover:text-paper transition-colors duration-300 px-8 py-4"
            >
              <span className="font-mono text-[12px] tracking-[0.24em] uppercase font-semibold">
                Schedule Your Confidential Consultation
              </span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
